import React from 'react';
import { cn } from '@/lib/utils';
import { useResponsiveMoveIcon, type MoveIconType } from '@/hooks/ui/use-responsive-move-icon';

export interface MoveCardButtonProps {
  className?: string;
  onClick?: (e: React.MouseEvent) => void;
  disabled?: boolean;
  variant?: 'default' | 'glass' | 'solid';
  size?: 'sm' | 'md' | 'lg';
  iconType?: 'auto' | MoveIconType;
  title?: string;
}

const MoveCardButton: React.FC<MoveCardButtonProps> = ({
  className,
  onClick,
  disabled = false,
  variant = 'glass',
  size = 'md',
  iconType = 'auto',
  title,
}) => {
  const { icon: IconComponent, title: autoTitle } = useResponsiveMoveIcon(iconType);

  // Size configurations
  const sizeConfig = {
    sm: {
      container: 'w-7 h-7 p-1.5',
      icon: 'h-3 w-3'
    },
    md: {
      container: 'w-8 h-8 p-1.5',
      icon: 'h-4 w-4'
    },
    lg: {
      container: 'w-10 h-10 p-2',
      icon: 'h-5 w-5'
    }
  };

  // Variant configurations - simplified and beautiful with force-hover support
  const variantConfig = {
    default: cn(
      'bg-white/15 dark:bg-black/15',
      'border border-white/25 dark:border-white/15',
      'hover:bg-white/25 dark:hover:bg-black/25',
      'hover:border-white/35 dark:hover:border-white/25',
      '[&.force-hover]:bg-white/25 dark:[&.force-hover]:bg-black/25',
      '[&.force-hover]:border-white/35 dark:[&.force-hover]:border-white/25',
      'shadow-sm backdrop-blur-sm'
    ),
    glass: cn(
      'bg-white/20 dark:bg-black/20',
      'border border-white/25 dark:border-white/15',
      'hover:bg-white/30 dark:hover:bg-black/30',
      'hover:border-white/40 dark:hover:border-white/25',
      '[&.force-hover]:bg-white/30 dark:[&.force-hover]:bg-black/30',
      '[&.force-hover]:border-white/40 dark:[&.force-hover]:border-white/25',
      'shadow-lg backdrop-blur-lg'
    ),
    solid: cn(
      'bg-gray-100 dark:bg-gray-800',
      'border border-gray-200 dark:border-gray-700',
      'hover:bg-gray-150 dark:hover:bg-gray-750',
      'hover:border-gray-250 dark:hover:border-gray-650',
      '[&.force-hover]:bg-gray-150 dark:[&.force-hover]:bg-gray-750',
      '[&.force-hover]:border-gray-250 dark:[&.force-hover]:border-gray-650',
      'shadow-md'
    )
  };

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!disabled && onClick) {
      onClick(e);
    }
  };

  return (
    <button
      onClick={handleClick}
      disabled={disabled}
      title={title || autoTitle}
      className={cn(
        'flex items-center justify-center rounded-full',
        'transition-all duration-300 ease-out',
        'hover:scale-105',
        'focus:outline-none focus:ring-2 focus:ring-blue-400/30 focus:ring-offset-1',
        sizeConfig[size].container,
        variantConfig[variant],
        disabled && 'opacity-50 cursor-not-allowed hover:scale-100',
        className
      )}
      style={{ 
        willChange: 'transform, opacity',
        backfaceVisibility: 'hidden'
      }}
      onMouseEnter={(e) => {
        e.currentTarget.classList.add('force-hover');
      }}
      onMouseLeave={(e) => {
        e.currentTarget.classList.remove('force-hover');
      }}
    >
      <IconComponent 
        className={cn(
          sizeConfig[size].icon,
          'text-gray-600 dark:text-gray-300',
          'transition-colors duration-300'
        )} 
      />
    </button>
  );
};

export default MoveCardButton; 